import { useMemo } from 'react';
import { tokens, Icon, Stack } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';
import { ReadOnlyBanner } from '@/components/share/ReadOnlyBanner';
import { QuotaCostBanner } from './QuotaCostBanner';

export interface BannerState {
  id: string;
  gvwId: string;
  severity: 'info' | 'warning' | 'error';
  priority: number; // Higher = shows on top
  visible: boolean;
  icon: string;
  title: string;
  body: string;
  actions?: Array<{
    label: string;
    primary?: boolean;
    disabled?: boolean;
    tooltip?: string;
    onPress: () => void;
  }>;
}

export interface PersistentBannersProps {
  className?: string;
}

/**
 * PersistentBanners — GVW-136..143.
 * 
 * Stacked persistent banners for offline, stale tiles, RLS deny, credentials,
 * quota exceeded, and cost budget states. Uses existing ReadOnlyBanner.
 */
export function PersistentBanners({ className }: PersistentBannersProps) {
  // Mock state (replace with actual platform SDK integration)
  const mockOfflineState = useViewerStore((state) => state.mockOfflineState);
  const mockStaleState = useViewerStore((state) => state.mockStaleState);
  const mockRlsState = useViewerStore((state) => state.mockRlsState);
  const mockCredentialsState = useViewerStore((state) => state.mockCredentialsState);
  const mockQuotaState = useViewerStore((state) => state.mockQuotaState);

  const banners = useMemo<BannerState[]>(() => {
    const result: BannerState[] = [];

    // GVW-136: Offline banner (warning)
    if (mockOfflineState?.active) {
      result.push({
        id: 'offline',
        gvwId: 'GVW-136',
        severity: mockOfflineState.extended ? 'error' : 'warning',
        priority: 50,
        visible: true,
        icon: 'cloud_off',
        title: 'You are offline',
        body: 'Cached layers continue to render. New tiles, copilot, and saves are paused.',
        actions: [{
          label: 'Retry',
          primary: true,
          onPress: () => {
            // Mock retry - would call platform retry
            console.log('[GVW-136] Retry offline connection');
          }
        }]
      });
    }

    // GVW-137: Stale tile banner (warning)  
    if (mockStaleState?.active) {
      result.push({
        id: 'stale-tiles',
        gvwId: 'GVW-137',
        severity: 'warning',
        priority: 40,
        visible: true,
        icon: 'sync_problem',
        title: 'Stale tiles',
        body: `${mockStaleState.layerName} was last refreshed ${mockStaleState.timeAgo}.`,
        actions: [{
          label: 'Refresh tiles',
          primary: true,
          onPress: () => {
            console.log('[GVW-137] Refresh tiles for layer');
          }
        }, {
          label: 'View integration health',
          onPress: () => {
            console.log('[GVW-137] Open integration health (GVW-148)');
          }
        }]
      });
    }

    // GVW-138: RLS deny banner (critical)
    if (mockRlsState?.active) {
      result.push({
        id: 'rls-deny',
        gvwId: 'GVW-138', 
        severity: 'error',
        priority: 100, // Highest priority (critical)
        visible: true,
        icon: 'block',
        title: 'Access denied',
        body: 'You do not have access to a critical layer in this view. Switch to an organization that has access, or request access.',
        actions: [{
          label: 'Switch organization',
          primary: true,
          onPress: () => {
            console.log('[GVW-138] Open org switcher (GVW-007)');
          }
        }, {
          label: 'Request access',
          onPress: () => {
            console.log('[GVW-138] Open access request');
          }
        }]
      });
    }

    // GVW-140: Quota exceeded banner (critical)
    if (mockQuotaState?.exceeded) {
      result.push({
        id: 'quota-exceeded',
        gvwId: 'GVW-140',
        severity: 'error', 
        priority: 90,
        visible: true,
        icon: 'error',
        title: 'Quota exceeded',
        body: `${mockQuotaState.quotaName} is over the limit for ${mockQuotaState.orgName}. New requests are paused until ${mockQuotaState.resetTime}.`,
        actions: [{
          label: 'View billing',
          primary: true,
          onPress: () => {
            console.log('[GVW-140] Open billing surface');
          }
        }]
      });
    }

    // GVW-141: Credentials expired banner (error)
    if (mockCredentialsState?.expired) {
      result.push({
        id: 'credentials-expired',
        gvwId: 'GVW-141',
        severity: 'error',
        priority: 80,
        visible: true,
        icon: 'key',
        title: 'Credentials expired',
        body: `${mockCredentialsState.providerName} credentials for ${mockCredentialsState.layerName} expired on ${mockCredentialsState.expiredDate}.`,
        actions: [{
          label: 'Rotate credentials',
          primary: true,
          onPress: () => {
            console.log('[GVW-141] Open Admin to rotate credentials');
          }
        }]
      });
    }

    return result.sort((a, b) => b.priority - a.priority);
  }, [mockOfflineState, mockStaleState, mockRlsState, mockCredentialsState, mockQuotaState]);

  const visibleBanners = banners.filter(banner => banner.visible);

  if (visibleBanners.length === 0) {
    return null;
  }

  return (
    <div className={className} style={{ width: '100%' }}>
      {/* GVW-143: Read-only banner - uses existing component */}
      <ReadOnlyBanner />
      
      {/* Other persistent banners */}
      {visibleBanners.map((banner) => (
        <aside
          key={banner.id}
          data-gvw-id={banner.gvwId}
          role={banner.severity === 'error' ? 'alert' : 'status'}
          aria-live="polite"
          style={{
            width: '100%',
            position: 'relative',
            zIndex: tokens.zIndex.panel,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: tokens.space[3],
            padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
            borderBottom: tokens.border.thin,
            borderLeft: `${tokens.space[1]}px solid ${(() => {
              switch (banner.severity) {
                case 'error': return 'var(--md-error)';
                case 'warning': return 'var(--md-warning)';
                case 'info': return 'var(--md-info)';
                default: return tokens.color.semantic.info;
              }
            })()}`,
            background: (() => {
              switch (banner.severity) {
                case 'error': return 'var(--md-error-container)';
                case 'warning': return 'var(--md-warning-container)';
                case 'info': return 'var(--md-info-container)';
                default: return tokens.color.surface[2];
              }
            })(),
            color: (() => {
              switch (banner.severity) {
                case 'error': return 'var(--md-on-error-container)';
                case 'warning': return 'var(--md-on-warning-container)';
                case 'info': return 'var(--md-on-info-container)';
                default: return tokens.color.ink.primary;
              }
            })(),
            fontFamily: tokens.font.family.ui,
          }}
        >
          <Stack direction="row" align="center" gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Icon name={banner.icon as any} size={20} decorative />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                fontWeight: tokens.font.weight.medium, 
                fontSize: tokens.font.size.sm,
                marginBottom: tokens.space[1]
              }}>
                {banner.title}
              </div>
              <div style={{ 
                fontSize: tokens.font.size.sm,
                opacity: 0.9
              }}>
                {banner.body}
              </div>
            </div>
          </Stack>
          
          {banner.actions && banner.actions.length > 0 ? (
            <Stack direction="row" gap={2} style={{ flexShrink: 0 }}>
              {banner.actions.map((action, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={action.onPress}
                  disabled={action.disabled}
                  title={action.tooltip}
                  style={{
                    minHeight: tokens.density.touchMin,
                    border: action.primary ? 'none' : tokens.border.thin,
                    borderRadius: tokens.radius.md,
                    background: action.primary ? 'var(--md-primary)' : 'transparent',
                    color: action.primary ? 'var(--md-on-primary)' : 'inherit',
                    paddingInline: tokens.space[3],
                    cursor: action.disabled ? 'not-allowed' : 'pointer',
                    fontSize: tokens.font.size.sm,
                    fontWeight: tokens.font.weight.medium,
                    opacity: action.disabled ? 0.6 : 1,
                  }}
                >
                  {action.label}
                </button>
              ))}
            </Stack>
          ) : null}
        </aside>
      ))}
      
      {/* GVW-139: Cost budget banner - integrated with quota/cost surfaces */}
      <QuotaCostBanner />
    </div>
  );
}